/**
 * Our own date and time picker, for when the next mission comes: a modal over
 * the agent's screen in the phone-login modal's colours — a month calendar
 * (only today through MAX_DAYS ahead can be picked; the server books no
 * further out), then the time, then Set. × or a tap outside closes it.
 */
import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import { ACTION_BTN_CLASS } from "./step-shell";

/** How far ahead they can pick (the server clamps to the same). */
const MAX_DAYS = 14;
const DAY_MS = 86_400_000;
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MINUTES = [0, 15, 30, 45];
/** The login modal's gold, and its edge. */
const GOLD = "#d6a94e";

/** Local midnight of the day `t` falls on. */
const midnight = (t: number) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** The time now (read in handlers and the tick, never during render). */
const clock = () => Date.now();

const LABEL_CLASS =
  "mb-2 block text-xs tracking-[1px] text-[#a99d86] uppercase";
const ROUND_BTN =
  "grid size-8 cursor-pointer place-items-center rounded-full text-[#f4eddb] hover:bg-white/10 disabled:invisible";
const STEP_BTN =
  "grid h-7 w-full cursor-pointer place-items-center rounded-[5px] text-[#a99d86] hover:bg-white/10 hover:text-[#f4eddb]";

export function SchedulePicker({
  from,
  onSet,
  onClose,
}: {
  /** when the picker was opened: nothing before it can be picked */
  from: number;
  onSet: (at: number) => void;
  /** closed without picking (back to Now / Tomorrow) */
  onClose: () => void;
}) {
  const first = midnight(from);
  const last = first + MAX_DAYS * DAY_MS;
  // the month on show, and the day and time picked (tomorrow, 9am to start)
  const [view, setView] = useState(() => {
    const d = new Date(from);
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [day, setDay] = useState(() => midnight(first + DAY_MS * 1.5));
  const [hour12, setHour12] = useState(9);
  const [minute, setMinute] = useState(0);
  const [pm, setPm] = useState(false);

  const at = (() => {
    const d = new Date(day);
    d.setHours((hour12 % 12) + (pm ? 12 : 0), minute, 0, 0);
    return d.getTime();
  })();
  // checked against the time now, not when it opened: it may sit open a
  // while, and Set must never look enabled for a time already gone
  const [now, setNow] = useState(from);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const tooFar = at > from + MAX_DAYS * DAY_MS;
  const ok = at > now && !tooFar;
  // between ticks: check the clock itself, and if the time has gone the
  // button says so
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = clock();
    setNow(t);
    if (at > t && !tooFar) onSet(at);
  };
  // Escape closes it, as × does
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // the month's grid: blanks up to its first weekday, then its days
  const start = new Date(view.y, view.m, 1);
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: start.getDay() }, () => null),
    ...Array.from({ length: days }, (_, i) =>
      new Date(view.y, view.m, i + 1).getTime(),
    ),
  ];
  const canBack = start.getTime() > first;
  const canOn = new Date(view.y, view.m + 1, 1).getTime() <= last;
  const shift = (n: number) =>
    setView(({ y, m }) => {
      const d = new Date(y, m + n, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const stepper = (
    label: string,
    value: string,
    up: () => void,
    down: () => void,
  ) => (
    <div className="flex flex-1 flex-col items-center">
      <button
        type="button"
        aria-label={`${label} up`}
        onClick={up}
        className={STEP_BTN}
      >
        <ChevronUp className="size-4" />
      </button>
      <span className="py-0.5 text-[24px] leading-none font-bold text-[#f4eddb] tabular-nums">
        {value}
      </span>
      <button
        type="button"
        aria-label={`${label} down`}
        onClick={down}
        className={STEP_BTN}
      >
        <ChevronDown className="size-4" />
      </button>
    </div>
  );

  return (
    // over the screen, the room dimmed behind it; a tap outside closes it.
    // `backwards`, not `both`: a style left on it by a finished entrance stops
    // iPhone Safari swipe-scrolling the form (see laptop-desktop.tsx)
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sm-schedule-title"
      onClick={onClose}
      style={
        {
          "--sm-accent-edge": `${GOLD}6b`,
          "--sm-btn-text": "#e6d3a4",
        } as CSSProperties
      }
      className="fixed inset-0 z-[60] grid animate-[smFadeIn_.2s_ease_backwards] place-items-center bg-black/70 p-4 font-sans"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="max-h-full w-full max-w-[360px] animate-[smSay_.3s_cubic-bezier(.2,.8,.3,1)_backwards] overflow-y-auto rounded-xl border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.96)] px-[22px] pt-[20px] pb-[18px] shadow-[0_20px_60px_rgba(0,0,0,.55)]"
      >
        <div className="mb-[14px] flex items-center justify-between">
          <h2
            id="sm-schedule-title"
            className="m-0 text-lg font-bold tracking-[0.2px] text-[#f4eddb]"
          >
            Schedule your next mission
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="cursor-pointer border-none bg-transparent p-1 text-[22px] leading-none text-[#a99d86]"
          >
            ×
          </button>
        </div>

        {/* the month */}
        <span className={LABEL_CLASS}>Day</span>
        <div className="rounded-lg border border-[var(--sm-accent-edge)] bg-black/35 p-2">
          <div className="mb-1 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              disabled={!canBack}
              onClick={() => shift(-1)}
              className={ROUND_BTN}
            >
              <ChevronLeft className="size-5" />
            </button>
            <span className="text-[15px] font-bold text-[#f4eddb]">
              {start.toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              type="button"
              aria-label="Next month"
              disabled={!canOn}
              onClick={() => shift(1)}
              className={ROUND_BTN}
            >
              <ChevronRight className="size-5" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((w, i) => (
              <span
                key={i}
                className="py-1 text-[10.5px] tracking-[1px] text-[#a99d86]"
              >
                {w}
              </span>
            ))}
            {cells.map((t, i) =>
              t === null ? (
                <span key={`b${i}`} />
              ) : (
                <button
                  key={t}
                  type="button"
                  disabled={t < first || t > last}
                  aria-pressed={t === day}
                  aria-label={new Date(t).toDateString()}
                  onClick={() => setDay(t)}
                  className={`grid aspect-square cursor-pointer place-items-center rounded-full text-[14px] font-semibold disabled:cursor-default disabled:opacity-25 ${
                    t === day
                      ? "bg-[#d6a94e] text-[#1a1410]"
                      : t === first
                        ? "text-[#f4eddb] ring-1 ring-[#d6a94e]/70 hover:bg-white/10"
                        : "text-[#f4eddb] hover:bg-white/10"
                  }`}
                >
                  {new Date(t).getDate()}
                </button>
              ),
            )}
          </div>
        </div>

        {/* the time */}
        <span className={`mt-[14px] ${LABEL_CLASS}`}>Time</span>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--sm-accent-edge)] bg-black/35 px-2 py-1">
          {stepper(
            "Hour",
            String(hour12),
            () => setHour12((h) => (h % 12) + 1),
            () => setHour12((h) => ((h + 10) % 12) + 1),
          )}
          <span className="text-[24px] font-bold text-[#f4eddb]">:</span>
          {stepper(
            "Minute",
            String(minute).padStart(2, "0"),
            () => setMinute((m) => MINUTES[(MINUTES.indexOf(m) + 1) % 4]),
            () => setMinute((m) => MINUTES[(MINUTES.indexOf(m) + 3) % 4]),
          )}
          <div className="ml-1 flex flex-col gap-1">
            {(["AM", "PM"] as const).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={pm === (p === "PM")}
                onClick={() => setPm(p === "PM")}
                className={`cursor-pointer rounded-[5px] px-2.5 py-1 text-[13px] font-bold ${
                  pm === (p === "PM")
                    ? "bg-[#d6a94e] text-[#1a1410]"
                    : "bg-white/10 text-[#a99d86]"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={!ok}
          className={`mt-[14px] w-full ${ACTION_BTN_CLASS} rounded-lg px-4 py-3 text-[14.5px] font-semibold disabled:opacity-60`}
        >
          {ok
            ? `Set for ${new Date(at).toLocaleString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}`
            : tooFar
              ? "Up to 2 weeks ahead"
              : "That time has passed"}
        </button>
      </form>
    </div>
  );
}
